import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Skeleton } from '@mui/material';

import { InfoLanguage } from '@bandido/shared/enums';
import { DescriptionType } from '@bandido/shared/enums';
import { useGetHotelNimbuxHotelById } from '@frontend/features/hotel-content-manager/queries';
import {
  HotelImageGallery,
  HotelBasicInfo,
  HotelDescription,
  HotelAmenities,
  ImageModal,
  hotelViewStyles
} from '@frontend/features/hotel-content/components/Detail/HotelView';
import { useHotelImagesModal } from '@frontend/features/hotel-content/hooks';

export const HotelContentViewGeneral = () => {
  const { hotelId } = useParams<{ hotelId: string }>();
  const { data: nimbuxHotel, isLoading } = useGetHotelNimbuxHotelById(Number(hotelId));
  const { i18n } = useTranslation();

  const images = nimbuxHotel?.medias ?? [];
  const imageModal = useHotelImagesModal(images);

  // TODO: get rank from google reviews
  const data = {
    rank: 2,
  };

  const hotelDescription = nimbuxHotel?.descriptions
    ?.find((description) => description.type === DescriptionType.GENERAL)
    ?.texts[i18n.language as InfoLanguage];

  if (isLoading) return <LoadingState />;

  return (
    <Paper elevation={0}>
      <Box sx={hotelViewStyles.container}>
        {images.length > 0 && (
          <HotelImageGallery
            images={images}
            onImageClick={imageModal.handleImageClick}
          />
        )}

        {nimbuxHotel?.name && (
          <HotelBasicInfo
            name={nimbuxHotel.name}
            rating={data.rank}
            impressions={nimbuxHotel.impressions}
          />
        )}

        <HotelDescription description={hotelDescription} />

        <HotelAmenities amenities={nimbuxHotel?.amenities || []} />

        <ImageModal
          open={imageModal.modalOpen}
          currentIndex={imageModal.currentImageIndex}
          images={images}
          onClose={imageModal.handleCloseModal}
          onNext={imageModal.handleNextImage}
          onPrev={imageModal.handlePrevImage}
        />
      </Box>
    </Paper>
  );
};

const LoadingState = () => (
  <Box sx={hotelViewStyles.container}>
    <Box display="flex" flexDirection="row" gap={1}>
      <Skeleton variant="rounded" width={716} height={600} />
      <Box display="flex" flexDirection="column" gap={1}>
        <Skeleton variant="rounded" width={358} height={195} />
        <Skeleton variant="rounded" width={358} height={195} />
        <Skeleton variant="rounded" width={358} height={195} />
      </Box>
    </Box>
    <Box display="flex" flexDirection="column" gap={1}>
      <Skeleton variant="rounded" width={270} height={32} />
      <Skeleton variant="rounded" width={200} height={22} />
    </Box>
    <Box display="flex" flexDirection="column" mt={2} gap={1}>
      <Skeleton variant="rounded" width={180} height={32} />
    </Box>
    <Box display="flex" flexDirection="column" mt={2} gap={1}>
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={850} height={15} />
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={900} height={15} />
      <Skeleton variant="rounded" width={850} height={15} />
      <Skeleton variant="rounded" width={700} height={15} />
    </Box>
  </Box>
);
